import { zodResolver } from '@hookform/resolvers/zod'
import type { BackofficeAuthRequestPasswordResetData } from '@repo/sdk/gen'
import { backofficeAuthRequestPasswordReset } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { ButtonLink } from '@repo/ui/commons/button-link'
import { Field, FieldError, FieldGroup, FieldLabel } from '@repo/ui/field'
import { startCooldown, useCooldown } from '@repo/ui/hooks/use-cooldown'
import { Input } from '@repo/ui/input'
import {
	AuthCard,
	AuthCardBody,
	AuthCardHeader
} from '@repo/ui/modules/auth/components/auth-card'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import i18n, { useTranslation } from '@/lib/i18n'

const ForgotPasswordSchema = z.object({
	email: z.email({ error: () => i18n.t('auth:validation.email_invalid') })
})

export const Route = createFileRoute('/(auth)/forgot-password')({
	component: AdminForgotPassword
})

function AdminForgotPassword() {
	const { t } = useTranslation()

	const form = useForm<z.infer<typeof ForgotPasswordSchema>>({
		resolver: zodResolver(ForgotPasswordSchema),
		defaultValues: { email: '' }
	})

	const requestReset = useMutation({
		mutationFn: (body: BackofficeAuthRequestPasswordResetData['body']) =>
			backofficeAuthRequestPasswordReset({ body }),
		// No user-enumeration: confirm regardless of whether the address exists.
		onSuccess: () => {
			toast.success(t('auth:forgot_password.email_sent_title'), {
				description: t('auth:forgot_password.email_sent_description')
			})
			startCooldown('forgot-password')
		}
	})

	return (
		<AuthCard>
			<AuthCardHeader
				description={t('auth:forgot_password.description')}
				title={t('auth:forgot_password.title')}
			/>
			<AuthCardBody>
				<form
					onSubmit={form.handleSubmit((data) =>
						requestReset.mutate({
							email: data.email,
							type: 'magic-link'
						})
					)}
				>
					<FieldGroup>
						{requestReset.isError && (
							<AuthErrorAlert
								onClose={() => requestReset.reset()}
								title={formatError(requestReset.error)}
							/>
						)}
						<Controller
							control={form.control}
							name='email'
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t('common:email')}
									</FieldLabel>
									<Input
										{...field}
										aria-invalid={fieldState.invalid}
										id={field.name}
										placeholder={t(
											'auth:email_placeholder'
										)}
										type='email'
									/>
									{fieldState.invalid && (
										<FieldError
											errors={[fieldState.error]}
										/>
									)}
								</Field>
							)}
						/>
						<Field>
							<SubmitButton
								isPending={requestReset.isPending}
								isSuccess={requestReset.isSuccess}
							/>
						</Field>
					</FieldGroup>
				</form>
				<div className='flex justify-center'>
					<ButtonLink to='/login' variant='link'>
						{t('auth:back_to_login')}
					</ButtonLink>
				</div>
			</AuthCardBody>
		</AuthCard>
	)
}

// Isolated leaf so the per-second cooldown tick re-renders only the button,
// never the card. The page writes the cooldown via startCooldown; only this
// reads it.
function SubmitButton(props: { isPending: boolean; isSuccess: boolean }) {
	const { t } = useTranslation()
	const cooldown = useCooldown('forgot-password')

	return (
		<Button
			disabled={props.isPending || cooldown.isCoolingDown}
			type='submit'
		>
			{props.isPending && <Spinner />}
			{!props.isPending &&
				cooldown.isCoolingDown &&
				t('auth:cooldown', { seconds: cooldown.secondsLeft })}
			{!(props.isPending || cooldown.isCoolingDown) &&
				t(
					props.isSuccess
						? 'auth:forgot_password.resend'
						: 'auth:forgot_password.submit'
				)}
		</Button>
	)
}
